import { inject, Injectable } from '@angular/core';
import { Option, Result } from '@sapphire/result';

import { ToastService } from '@ps/core-overlay';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';
import { AdvancedFeasibilityFilters, Survey } from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import { SurveyCreationSelectorsFacade } from '../../state/survey-creation.selectors.facade';
import {
  buildChildSwitchContext,
  ChildSwitchContext,
  ChildSwitchViolation,
  validateChildSwitch,
  validateLocaleAdd,
} from '../../state/core/validation/child-switch.validations';

type ChildSwitchValidator = (
  survey: Option<Survey>,
  context: ChildSwitchContext,
) => Result<void, ChildSwitchViolation>;

@Injectable({ providedIn: 'root' })
export class ChildSwitchGuardService {
  private readonly _toastService = inject(ToastService);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _selectors = inject(SurveyCreationSelectorsFacade);

  public allowsSwitch(
    survey: Nullable<Survey>,
    advancedFilters: AdvancedFeasibilityFilters,
  ): boolean {
    return this._allows(survey, advancedFilters, validateChildSwitch);
  }

  public allowsAdd(
    survey: Nullable<Survey>,
    advancedFilters: AdvancedFeasibilityFilters,
  ): boolean {
    return this._allows(survey, advancedFilters, validateLocaleAdd);
  }

  private _allows(
    survey: Nullable<Survey>,
    advancedFilters: AdvancedFeasibilityFilters,
    validate: ChildSwitchValidator,
  ): boolean {
    const buyerSettings = this._userSettingsSelectors.buyerSettings();

    return validate(
      Option.from(survey),
      buildChildSwitchContext({
        advancedFilters,
        isRespondentStatusFilterEnabled:
          !!buyerSettings.enableRespondentStatusFilter,
        isManualProcessingEnabled: !!buyerSettings.enableManualProcessing,
        isFeasibilityLoading: this._selectors.feasibilityLoading(),
        uniqueLinks: this._selectors.launchForm().value.uniqueLinks,
      }),
    ).match({
      ok: () => true,
      err: (violation) => {
        this._notify(violation);
        return false;
      },
    });
  }

  private _notify({ message, severity }: ChildSwitchViolation): void {
    if (severity === 'warning') {
      this._toastService.warning(message);
      return;
    }

    this._toastService.error(message);
  }
}
